'use strict'

import CXICCProfile from './CXICCProfile.js'

/**
 * CXICCProfileManager
 * Workflow:
 * - Register ICC profiles by type.
 * - Read/write default profile names to local storage.
 */
class CXICCProfileManager {
    static PREFERENCES_KEY = 'iccProfiles'

    static ICCProfileKey = ['RGBDefaultProfile', 'CMYKProfileName', 'LabProfileName']

    static installedProfiles = new Map()

    /**
     * Return the profiles registered for a profile type.
     * @param {{getValue:Function}} profileType
     * @returns {CXICCProfile[]}
     */
    static getProfiles(profileType) {
        const key = profileType?.getValue ? profileType.getValue() : profileType
        if (!CXICCProfileManager.installedProfiles.has(key)) {
            CXICCProfileManager.installedProfiles.set(key, [])
        }
        return CXICCProfileManager.installedProfiles.get(key)
    }

    /**
     * Register a single profile.
     * @param {CXICCProfile} profile
     */
    static installProfile(profile) {
        if (!profile) return
        const profileType = profile.getProfileType()
        CXICCProfileManager.getProfiles(profileType).push(profile)
    }

    /**
     * Register multiple profiles.
     * @param {CXICCProfile[]} profiles
     */
    static installProfiles(profiles) {
        ;(profiles || []).forEach((profile) => CXICCProfileManager.installProfile(profile))
    }

    /**
     * Find a profile by type and name.
     * @param {{getValue:Function}} profileType
     * @param {string} profileName
     * @returns {CXICCProfile|null}
     */
    static getProfile(profileType, profileName) {
        if (!profileName) return null
        const profiles = CXICCProfileManager.getProfiles(profileType)
        return profiles.find((profile) => profile.getProfileName() === profileName) || null
    }

    /**
     * Resolve the default profile for a profile type.
     * @param {{getValue:Function}} profileType
     * @returns {CXICCProfile|null}
     */
    static defaultProfile(profileType) {
        const profiles = CXICCProfileManager.getProfiles(profileType)
        if (!profiles.length) return null

        const key = CXICCProfileManager._preferenceKeyForType(profileType)
        const defaultProfileName = CXICCProfileManager._getPreference(key)
        return CXICCProfileManager.getProfile(profileType, defaultProfileName) || profiles[0]
    }

    /**
     * Store the default profile for a profile type.
     * @param {{getValue:Function}} profileType
     * @param {string} profileName
     */
    static setDefaultProfile(profileType, profileName) {
        if (!profileName) return
        const key = CXICCProfileManager._preferenceKeyForType(profileType)
        CXICCProfileManager._setPreference(key, profileName)
    }

    /**
     * Resolve preference key for a profile type.
     * @param {{getValue:Function}} profileType
     * @returns {string|null}
     */
    static _preferenceKeyForType(profileType) {
        const index = profileType?.getValue ? profileType.getValue() : profileType
        return CXICCProfileManager.ICCProfileKey[index] || null
    }

    /**
     * Read a preference from storage.
     * @param {string} key
     * @returns {string|null}
     */
    static _getPreference(key) {
        if (!key || typeof window === 'undefined' || !window.localStorage) return null
        try {
            const raw = window.localStorage.getItem(CXICCProfileManager.PREFERENCES_KEY)
            const data = raw ? JSON.parse(raw) : {}
            return data[key] || null
        } catch {
            return null
        }
    }

    /**
     * Write a preference to storage.
     * @param {string} key
     * @param {string} value
     */
    static _setPreference(key, value) {
        if (!key || typeof window === 'undefined' || !window.localStorage) return
        try {
            const raw = window.localStorage.getItem(CXICCProfileManager.PREFERENCES_KEY)
            const data = raw ? JSON.parse(raw) : {}
            data[key] = value
            window.localStorage.setItem(CXICCProfileManager.PREFERENCES_KEY, JSON.stringify(data))
        } catch {
            // Ignore storage failures (private mode, denied, etc.).
        }
    }
}

// Register default profiles at load time.
CXICCProfileManager.installProfiles([CXICCProfile.sRGB, CXICCProfile.cieLabD50])

export default CXICCProfileManager
